/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { gql, useMutation, useLazyQuery } from '@apollo/client';
import { useTreatments } from '@splitsoftware/splitio-react';
import { UaParserDom, Devices, Os } from '@userzoomplatform/ua-parser';

import { usePageView, useToken } from '../../hooks';
import { CheckRestrictionsResponse } from '../../types/restrictions';
import { Splits } from '../../utils/constants';

const checkRestrictionsMutation = gql`
	mutation checkRestrictions($input: MobileRestrictionsInput) {
		checkRestrictions(input: $input) {
			success
			redirectUrl
		}
	}
`;

const getEnableNewTasksQuery = gql`
	query {
		participant {
			enableNewTasks
		}
	}
`;

const CheckRestrictions = (): null => {
	const history = useHistory();
	const token = useToken();
	usePageView('/check-restrictions');

	const treatments = useTreatments([Splits.newTasksUi]);
	const tasksUiEnabled = treatments[Splits.newTasksUi].treatment === 'on';

	const [getEnableNewTasks, { loading: loadingNewTasks, data: dataNewTasks }] = useLazyQuery(getEnableNewTasksQuery);

	const [checkRestrictions, { loading, error, data }] = useMutation<CheckRestrictionsResponse>(checkRestrictionsMutation);

	useEffect(() => {
		const uaParser = new UaParserDom(navigator.userAgent);
		const { type, os, browser } = uaParser.getDevice();
		if (type === Devices.Tablet && os?.name === Os.Ios) {
			checkRestrictions({
				variables: {
					input: {
						device: {
							type,
							browser: { name: browser?.name, version: browser?.version },
							os: { name: os.name, version: os.version },
						},
					},
				},
			});
		} else {
			checkRestrictions();
		}
	}, [checkRestrictions]);

	useEffect(() => {
		if (tasksUiEnabled) {
			getEnableNewTasks();
		}
	}, [tasksUiEnabled, getEnableNewTasks]);

	useEffect(() => {
		if (error) {
			history.replace('/forbidden');
		} else if (data?.checkRestrictions) {
			const { redirectUrl, success } = data.checkRestrictions;

			if (!success) {
				history.replace(`/study-restrictions?token=${token}`);
			} else if (dataNewTasks?.participant?.enableNewTasks) {
				history.replace(`/init-participant?token=${token}`);
			} else if (redirectUrl) {
				window.location.href = redirectUrl;
			} else {
				history.replace('/forbidden');
			}
		}
	}, [loading, error, data, history, token, dataNewTasks, loadingNewTasks]);

	return null;
};

export default CheckRestrictions;
